import React, { useState, useEffect } from "react";
import { useAuth } from "../../context/AuthContext";
import { apiGet, apiPut } from "../../services/api";

interface OrgConfig {
  buildingTypes: { id: number; label: string }[];
  roles: { id: string; label: string }[];
}

const DEFAULT_BUILDING_TYPES = [
  { id: 1, label: "Office" },
  { id: 2, label: "Warehouse" },
  { id: 3, label: "Store" },
  { id: 4, label: "Distribution Center" },
  { id: 5, label: "Restaurant" },
  { id: 6, label: "Service Center" },
  { id: 7, label: "Manufacturing" },
  { id: 8, label: "Showroom" },
  { id: 9, label: "Other" },
];

const DEFAULT_ROLES = [
  { id: "admin", label: "Admin" },
  { id: "manager", label: "Manager" },
  { id: "employee", label: "Employee" },
];

export default function OrgConfigTab() {
  const { user } = useAuth();
  const orgId = user?.organizationId;
  const [config, setConfig] = useState<OrgConfig>({ buildingTypes: [], roles: [] });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [status, setStatus] = useState<{ type: "success" | "error"; message: string } | null>(null);

  // New building type form
  const [newBuildingId, setNewBuildingId] = useState("");
  const [newBuildingLabel, setNewBuildingLabel] = useState("");

  // New role form
  const [newRoleId, setNewRoleId] = useState("");
  const [newRoleLabel, setNewRoleLabel] = useState("");

  useEffect(() => {
    if (orgId) loadConfig();
  }, [orgId]);

  async function loadConfig() {
    try {
      const data = await apiGet<OrgConfig>(`/organizations/${orgId}/config`);
      setConfig({
        buildingTypes: data.buildingTypes || [],
        roles: data.roles || [],
      });
    } catch {
      setConfig({ buildingTypes: [], roles: [] });
    } finally {
      setLoading(false);
    }
  }

  const saveConfig = async (updated: OrgConfig) => {
    setStatus(null);
    setSaving(true);
    try {
      await apiPut(`/organizations/${orgId}/config`, updated);
      setConfig(updated);
      setStatus({ type: "success", message: "Configuration saved" });
    } catch (err) {
      setStatus({ type: "error", message: err instanceof Error ? err.message : "Failed to save" });
    } finally {
      setSaving(false);
    }
  };

  const addBuildingType = () => {
    const id = parseInt(newBuildingId);
    if (!id || id < 10 || !newBuildingLabel.trim()) return;
    if (config.buildingTypes.some((b) => b.id === id) || DEFAULT_BUILDING_TYPES.some((b) => b.id === id)) return;

    const updated = {
      ...config,
      buildingTypes: [...config.buildingTypes, { id, label: newBuildingLabel.trim() }].sort((a, b) => a.id - b.id),
    };
    saveConfig(updated);
    setNewBuildingId("");
    setNewBuildingLabel("");
  };

  const removeBuildingType = (id: number) => {
    const updated = {
      ...config,
      buildingTypes: config.buildingTypes.filter((b) => b.id !== id),
    };
    saveConfig(updated);
  };

  const addRole = () => {
    if (!newRoleId.trim() || !newRoleLabel.trim()) return;
    const roleId = newRoleId.trim().toLowerCase().replace(/\s+/g, "-");
    if (config.roles.some((r) => r.id === roleId) || DEFAULT_ROLES.some((r) => r.id === roleId)) return;

    const updated = {
      ...config,
      roles: [...config.roles, { id: roleId, label: newRoleLabel.trim() }],
    };
    saveConfig(updated);
    setNewRoleId("");
    setNewRoleLabel("");
  };

  const removeRole = (id: string) => {
    const updated = {
      ...config,
      roles: config.roles.filter((r) => r.id !== id),
    };
    saveConfig(updated);
  };

  if (loading) return <div className="settings-loading">Loading configuration...</div>;

  const allBuildingTypes = [...DEFAULT_BUILDING_TYPES, ...config.buildingTypes].sort((a, b) => a.id - b.id);
  const allRoles = [...DEFAULT_ROLES, ...config.roles];

  return (
    <div>
      {status && (
        <div style={{ marginBottom: "1rem" }}>
          <span className={`save-status ${status.type}`}>{status.message}</span>
        </div>
      )}

      {/* Building Types */}
      <div className="settings-section">
        <h2>Building Types</h2>
        <p className="section-desc">
          Types 1–9 are system defaults. Add custom types starting from 10.
        </p>

        <table className="data-table" style={{ marginBottom: "1.25rem" }}>
          <thead>
            <tr>
              <th>ID</th>
              <th>Label</th>
              <th>Source</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {allBuildingTypes.map((bt) => {
              const isDefault = bt.id <= 9;
              return (
                <tr key={bt.id}>
                  <td><code>{bt.id}</code></td>
                  <td>{bt.label}</td>
                  <td>
                    <span className={`status-badge ${isDefault ? "active" : "invited"}`}>
                      {isDefault ? "System" : "Custom"}
                    </span>
                  </td>
                  <td>
                    {!isDefault && (
                      <div className="table-actions">
                        <button className="delete" onClick={() => removeBuildingType(bt.id)}>Remove</button>
                      </div>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>

        <div style={{ display: "flex", gap: "0.75rem", alignItems: "flex-end" }}>
          <div className="form-group" style={{ marginBottom: 0, width: "80px" }}>
            <label htmlFor="new-bt-id">ID (10+)</label>
            <input
              id="new-bt-id"
              type="number"
              min="10"
              value={newBuildingId}
              onChange={(e) => setNewBuildingId(e.target.value)}
              placeholder="10"
            />
          </div>
          <div className="form-group" style={{ marginBottom: 0, flex: 1 }}>
            <label htmlFor="new-bt-label">Label</label>
            <input
              id="new-bt-label"
              type="text"
              value={newBuildingLabel}
              onChange={(e) => setNewBuildingLabel(e.target.value)}
              placeholder="e.g. Pop-up Shop"
            />
          </div>
          <button
            className="btn btn-primary"
            style={{ width: "auto", height: "38px" }}
            onClick={addBuildingType}
            disabled={saving}
          >
            Add
          </button>
        </div>
      </div>

      {/* Roles */}
      <div className="settings-section">
        <h2>Employee Roles</h2>
        <p className="section-desc">
          Default roles (Admin, Manager, Employee) cannot be removed. Add custom roles for your organization.
        </p>

        <table className="data-table" style={{ marginBottom: "1.25rem" }}>
          <thead>
            <tr>
              <th>ID</th>
              <th>Label</th>
              <th>Source</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {allRoles.map((role) => {
              const isDefault = DEFAULT_ROLES.some((r) => r.id === role.id);
              return (
                <tr key={role.id}>
                  <td><code>{role.id}</code></td>
                  <td>{role.label}</td>
                  <td>
                    <span className={`status-badge ${isDefault ? "active" : "invited"}`}>
                      {isDefault ? "System" : "Custom"}
                    </span>
                  </td>
                  <td>
                    {!isDefault && (
                      <div className="table-actions">
                        <button className="delete" onClick={() => removeRole(role.id)}>Remove</button>
                      </div>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>

        <div style={{ display: "flex", gap: "0.75rem", alignItems: "flex-end" }}>
          <div className="form-group" style={{ marginBottom: 0, width: "150px" }}>
            <label htmlFor="new-role-id">Role ID</label>
            <input
              id="new-role-id"
              type="text"
              value={newRoleId}
              onChange={(e) => setNewRoleId(e.target.value)}
              placeholder="e.g. shift-lead"
            />
          </div>
          <div className="form-group" style={{ marginBottom: 0, flex: 1 }}>
            <label htmlFor="new-role-label">Display Name</label>
            <input
              id="new-role-label"
              type="text"
              value={newRoleLabel}
              onChange={(e) => setNewRoleLabel(e.target.value)}
              placeholder="e.g. Shift Lead"
            />
          </div>
          <button
            className="btn btn-primary"
            style={{ width: "auto", height: "38px" }}
            onClick={addRole}
            disabled={saving}
          >
            Add
          </button>
        </div>
      </div>
    </div>
  );
}
